import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useQueries } from '@tanstack/react-query';

import { checkDiscountCode } from 'api/jalapeno';
import useAccountQuery from 'api/queries/useAccountQuery';
import useCustomerPromoNanoBanner from 'features/site/header/nanobars/CustomerPromoNanoBanner/useCustomerPromoNanoBanner';
import { setSignupLocalStorage } from 'utils/signupUtils';

import { useApplyOptimizelyPromo } from './useApplyOptimizelyPromo';
import { useIsSignupNanoBannerRoute } from './useIsSignupNanoBannerRoute';
import { getPrioritizedPromoCodes, persistAppliedPromoCode, trackAutoPromoCodeApply } from './utils';

export const PromoCodeContext = createContext(null);

/**
 * Use the promo code context.
 * You can:
 * 1. Get the currently applied promo data with appliedPromoCodeData
 * 2. Set an applied promo's data with setAppliedPromoCodeData(), like after a form submission. You should validate the promo before setting it.
 * 3. Persist the promo code (to local storage) with persistAppliedPromoCode(). You'll generally want to do this alongside setAppliedPromoCodeData().
 * @example const { appliedPromoCodeData, setAppliedPromoCodeData, persistAppliedPromoCode, renderSignupNanoBanner } = usePromoCodeContext();
 * @returns {PromoCodeContext}
 */
export function usePromoCodeContext() {
    return useContext(PromoCodeContext);
}

function PromoCodeProvider({ children }) {
    const [appliedPromoCodeData, setAppliedPromoCodeData] = useState({});
    const [renderSignupNanoBanner, setRenderSignupNanoBanner] = useState(false);
    const trackedFailedPriorityApplyRef = useRef(false);
    const isSignupNanoBannerRoute = useIsSignupNanoBannerRoute();
    const { data: account } = useAccountQuery();

    const customerPromoNanoBanner = useCustomerPromoNanoBanner();

    const prioritizedPromoCodes = getPrioritizedPromoCodes();

    const queries = useQueries({
        queries: prioritizedPromoCodes?.map((code) => ({
            queryKey: code === 'MMROUNDS30' ? ['promo', code, account?.customerID || '15181875'] : ['promo', code],
            queryFn: () => {
                let customerID = null;
                if (code === 'MMROUNDS30') {
                    customerID = account?.customerID || '15181875';
                }
                return checkDiscountCode(customerID, code);
            },
            enabled: isSignupNanoBannerRoute && prioritizedPromoCodes?.length > 0,
            refetchOnWindowFocus: false,
            staleTime: 1000 * 60 * 60, // 1 hour
        })),
    });

    const queriesFetching = queries.some((result) => result.isFetching);

    useApplyOptimizelyPromo({
        queriedPromoCodes: queries,
        queriedPromoCodesFetching: queriesFetching,
        setAppliedPromoCodeData,
    });

    useEffect(() => {
        if (queriesFetching) return;

        const priorityPromo = queries.find((query) => query?.data?.data?.disc?.valid);

        if (priorityPromo) {
            setAppliedPromoCodeData(priorityPromo.data.data);
            persistAppliedPromoCode(priorityPromo.data.data.disc.code);
        } else {
            if (
                queries.length > 0 &&
                !trackedFailedPriorityApplyRef.current &&
                queries.every((query) => query.isFetched)
            ) {
                trackedFailedPriorityApplyRef.current = true;
                const attemptedCode = queries[0]?.data?.data?.disc?.code ?? prioritizedPromoCodes[0];

                trackAutoPromoCodeApply({
                    promoCode: attemptedCode,
                    source: 'priority',
                    valid: false,
                });
            }

            setSignupLocalStorage({ discountCode: '' });
        }
    }, [queriesFetching]);

    useEffect(() => {
        setRenderSignupNanoBanner(isSignupNanoBannerRoute && !!appliedPromoCodeData?.disc?.valid);
    }, [appliedPromoCodeData?.disc?.valid, isSignupNanoBannerRoute]);

    const value = useMemo(
        () => ({
            appliedPromoCodeData,
            setAppliedPromoCodeData,
            persistAppliedPromoCode,
            renderSignupNanoBanner,
            customerPromoNanoBanner,
        }),
        [
            appliedPromoCodeData,
            setAppliedPromoCodeData,
            persistAppliedPromoCode,
            renderSignupNanoBanner,
            customerPromoNanoBanner,
        ]
    );

    return <PromoCodeContext.Provider value={value}>{children}</PromoCodeContext.Provider>;
}

export default PromoCodeProvider;
